import { useCallback, useEffect, useRef } from 'react';

/**
 * Returns a stable function that delays invoking `fn` until `delay` ms have
 * passed since the last call. Timer resets on every call.
 *
 * The returned function exposes:
 *   .cancel()  — cancel any pending invocation
 *   .flush(...args) — invoke immediately and cancel the pending timer
 *
 * The latest `fn` is always used, so closures stay fresh without re-creating
 * the debounced function. Pending timers are cleared on unmount.
 */
export function useDebouncedCallback(fn, delay) {
  const fnRef = useRef(fn);
  const timerRef = useRef(null);

  useEffect(() => { fnRef.current = fn; }, [fn]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const debounced = useCallback((...args) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      fnRef.current(...args);
    }, delay);
  }, [delay]);

  debounced.cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  debounced.flush = useCallback((...args) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    fnRef.current(...args);
  }, []);

  return debounced;
}
